import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Polygon, Tooltip, useMapEvents } from 'react-leaflet';
import { LatLngExpression } from 'leaflet';
import * as turf from '@turf/turf';
import { useMap } from '../../contexts/MapContext';
import gazaBoundary from '../../data/gaza-boundary.json';

const GazaOverlay: React.FC = () => {
  const { userLocation, gazaPosition, setGazaPosition, setIsOverlayDragging } = useMap();
  const [positions, setPositions] = useState<LatLngExpression[]>([]);
  const [isLocked, setIsLocked] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartMouse, setDragStartMouse] = useState<{ lat: number; lng: number } | null>(null);
  const [dragStartCenter, setDragStartCenter] = useState<{ lat: number; lng: number } | null>(null);
  const polygonRef = useRef<any>(null);
  const touchStartRef = useRef<{ lat: number; lng: number } | null>(null);

  // Calculate the centroid of Gaza boundary
  const gazaCentroid = useMemo(() => {
    if (!gazaBoundary?.geometry?.coordinates?.[0]) return [31.5, 34.45];
    const polygon = turf.polygon(gazaBoundary.geometry.coordinates);
    const center = turf.centroid(polygon);
    return [center.geometry.coordinates[1], center.geometry.coordinates[0]];
  }, []);

  // Handle map events for dragging
  const map = useMapEvents({
    mousemove: (e) => {
      if (!isLocked && isDragging && dragStartMouse && dragStartCenter) {
        // Calculate how much the mouse has moved
        const latDiff = e.latlng.lat - dragStartMouse.lat;
        const lngDiff = e.latlng.lng - dragStartMouse.lng;
        
        // Apply the movement to the original center position
        const newLat = dragStartCenter.lat + latDiff;
        const newLng = dragStartCenter.lng + lngDiff;
        
        setGazaPosition([newLat, newLng]);
      }
    },
    mouseup: () => {
      if (isDragging) {
        setIsDragging(false);
        setDragStartMouse(null);
        setDragStartCenter(null);
        setIsOverlayDragging(false);
        // Re-enable all map interactions
        map.dragging.enable();
        map.touchZoom.enable();
        map.scrollWheelZoom.enable();
        map.doubleClickZoom.enable();
        map.boxZoom.enable();
        map.keyboard.enable();
      }
    }
  });

  // Handle touch events separately using useEffect
  useEffect(() => {
    if (!map || !polygonRef.current) return;

    const handlePolygonTouchStart = (e: TouchEvent) => {
      if (!isLocked) {
        e.preventDefault();
        e.stopPropagation();
        
        setIsDragging(true);
        setIsOverlayDragging(true);
        
        // Get the first touch point
        const touch = e.touches[0];
        if (!touch) return;
        
        const latlng = map.containerPointToLatLng([touch.clientX, touch.clientY]);
        
        // Store the initial touch position
        touchStartRef.current = {
          lat: latlng.lat,
          lng: latlng.lng
        };
        
        // Store the current center position of the overlay
        const currentCenter = gazaPosition || userLocation;
        const [centerLat, centerLng] = Array.isArray(currentCenter) 
          ? currentCenter 
          : [currentCenter!.lat, currentCenter!.lng];
        
        setDragStartCenter({
          lat: centerLat,
          lng: centerLng
        });
        
        // Disable map interactions while dragging overlay
        map.dragging.disable();
        map.touchZoom.disable();
        map.scrollWheelZoom.disable();
        map.doubleClickZoom.disable();
        map.boxZoom.disable();
        map.keyboard.disable();
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isLocked && isDragging && touchStartRef.current && dragStartCenter) {
        // Prevent default to stop map panning
        e.preventDefault();
        e.stopPropagation();
        
        // Get the first touch point
        const touch = e.touches[0];
        if (!touch) return;
        
        const latlng = map.containerPointToLatLng([touch.clientX, touch.clientY]);
        
        // Calculate how much the touch has moved
        const latDiff = latlng.lat - touchStartRef.current.lat;
        const lngDiff = latlng.lng - touchStartRef.current.lng;
        
        // Apply the movement to the original center position
        const newLat = dragStartCenter.lat + latDiff;
        const newLng = dragStartCenter.lng + lngDiff;
        
        setGazaPosition([newLat, newLng]);
      }
    };

    const handleTouchEnd = () => {
      if (isDragging) {
        setIsDragging(false);
        touchStartRef.current = null;
        setDragStartCenter(null);
        setIsOverlayDragging(false);
        // Re-enable all map interactions
        map.dragging.enable();
        map.touchZoom.enable();
        map.scrollWheelZoom.enable();
        map.doubleClickZoom.enable();
        map.boxZoom.enable();
        map.keyboard.enable();
      }
    };

    // Get the polygon element
    const polygonElement = (polygonRef.current as any)?._path;
    
    if (polygonElement) {
      // Add touch event listeners to the polygon element
      polygonElement.addEventListener('touchstart', handlePolygonTouchStart, { passive: false });
    }

    // Add touch event listeners to the map container for move and end
    const mapContainer = map.getContainer();
    mapContainer.addEventListener('touchmove', handleTouchMove, { passive: false });
    mapContainer.addEventListener('touchend', handleTouchEnd);

    return () => {
      if (polygonElement) {
        polygonElement.removeEventListener('touchstart', handlePolygonTouchStart);
      }
      mapContainer.removeEventListener('touchmove', handleTouchMove);
      mapContainer.removeEventListener('touchend', handleTouchEnd);
    };
  }, [map, polygonRef, isLocked, isDragging, gazaPosition, userLocation, setGazaPosition, setIsOverlayDragging]);

  // Transform Gaza boundary based on position
  useEffect(() => {
    if (!userLocation || !gazaBoundary?.geometry?.coordinates?.[0]) return;

    const targetPosition = gazaPosition || userLocation;
    const [targetLat, targetLng] = Array.isArray(targetPosition) 
      ? targetPosition 
      : [targetPosition.lat, targetPosition.lng];

    // Calculate translation offset from Gaza's actual center
    const [centerLat, centerLng] = gazaCentroid;
    const latOffset = targetLat - centerLat;
    const lngOffset = targetLng - centerLng;

    // Apply translation to all coordinates
    const transformedCoordinates = gazaBoundary.geometry.coordinates[0].map(coord => {
      return [
        coord[1] + latOffset,
        coord[0] + lngOffset
      ] as LatLngExpression;
    });

    setPositions(transformedCoordinates);

    // Set initial Gaza position
    if (!gazaPosition) {
      setGazaPosition(userLocation);
    }
  }, [userLocation, gazaPosition, gazaCentroid, setGazaPosition]);

  // Show tooltip for first-time users
  useEffect(() => {
    const hasSeenTooltip = localStorage.getItem('hasSeenOverlayTooltip');
    if (!hasSeenTooltip && positions.length > 0) {
      setTimeout(() => setShowTooltip(true), 1000);
    }
  }, [positions]);

  const handlePolygonClick = (e: any) => {
    // Only toggle lock if we're not dragging
    if (!isDragging) {
      e.originalEvent.stopPropagation();
      setIsLocked(!isLocked);
      
      // Hide tooltip after first interaction
      if (showTooltip) {
        setShowTooltip(false);
        localStorage.setItem('hasSeenOverlayTooltip', 'true');
      }
    }
  };

  const handleMouseDown = (e: any) => {
    if (!isLocked) {
      e.originalEvent.stopPropagation();
      e.originalEvent.preventDefault();
      
      setIsDragging(true);
      setIsOverlayDragging(true);
      
      // Store the initial mouse position
      setDragStartMouse({
        lat: e.latlng.lat,
        lng: e.latlng.lng
      });
      
      // Store the current center position of the overlay
      const currentCenter = gazaPosition || userLocation;
      const [centerLat, centerLng] = Array.isArray(currentCenter) 
        ? currentCenter 
        : [currentCenter!.lat, currentCenter!.lng];
      
      setDragStartCenter({
        lat: centerLat,
        lng: centerLng
      });
      
      // Disable all map interactions while dragging overlay
      map.dragging.disable();
      map.touchZoom.disable();
      map.scrollWheelZoom.disable();
      map.doubleClickZoom.disable();
      map.boxZoom.disable();
      map.keyboard.disable();
    }
  };


  const pathOptions = {
    color: isLocked ? 'rgba(204, 0, 0, 0.8)' : 'rgba(34, 139, 34, 0.8)',
    weight: isLocked ? 2 : 3,
    opacity: 0.8,
    fillColor: isLocked ? 'rgba(128, 128, 128, 0.4)' : 'rgba(128, 128, 128, 0.3)',
    fillOpacity: isLocked ? 0.4 : 0.3,
    dashArray: isLocked ? '5, 5' : '10, 5',
    className: 'gaza-overlay',
  };

  if (positions.length === 0) return null;

  return (
    <>
      <Polygon
        ref={polygonRef}
        positions={positions}
        pathOptions={pathOptions}
        eventHandlers={{
          click: handlePolygonClick,
          mousedown: handleMouseDown,
        }}
      >
        {showTooltip && (
          <Tooltip permanent direction="top" offset={[0, -20]}>
            <div style={{ textAlign: 'center' }}>
              <strong>Tap to unlock</strong><br />
              Then drag to move the Gaza overlay
            </div>
          </Tooltip>
        )}
      </Polygon>
      
      {!isLocked && (
        <div 
          style={{
            position: 'fixed',
            top: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: isDragging ? 'rgba(204, 0, 0, 0.9)' : 'rgba(34, 139, 34, 0.9)',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '4px',
            zIndex: 10000,
            pointerEvents: 'none',
            fontSize: '14px',
            fontWeight: 'bold',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            transition: 'background 0.3s ease',
          }}
        >
          {isDragging ? '📍 Dragging overlay...' : '🔓 Overlay Unlocked - Tap to Lock'}
        </div>
      )}
    </>
  );
};

export default GazaOverlay;